import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import {
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { getKitchenStatus } from "apis/orderAPIs";
import {
  ageText,
  dealIntoColumns,
  docketSummary,
  hasProblems,
  isDeviceStale,
  kdsChip,
  severityColor,
  severityLabel,
  shortOrderNumber,
  visibleStatuses,
} from "util/kitchenStatus";

const REFRESH_MS = 15000;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    // The app ships no CssBaseline, so plain elements would fall back to serif.
    fontFamily: theme.typography.fontFamily,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  subtitleProblem: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  controls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  devices: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  columns: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
  },
  column: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  ticket: {
    padding: theme.spacing(2),
    borderRadius: 14,
    backgroundColor: theme.palette.background.paper,
    border: `1.5px solid ${theme.palette.divider}`,
  },
  ticketProblem: {
    border: `4px solid ${theme.palette.error.main}`,
  },
  ticketInProgress: {
    border: `2.5px solid ${theme.palette.warning.main}`,
  },
  ticketHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  table: {
    fontWeight: 700,
    fontSize: "1.4rem",
    lineHeight: 1.2,
  },
  orderRef: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    fontVariantNumeric: "tabular-nums",
  },
  ticketMeta: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: theme.spacing(0.5),
  },
  age: {
    fontWeight: 700,
    fontSize: "1.1rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  ageProblem: {
    color: theme.palette.error.main,
  },
  dish: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
  },
  dishBody: {
    minWidth: 0,
  },
  dishName: {
    fontSize: "1.05rem",
    overflowWrap: "anywhere",
  },
  dishNote: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    marginTop: 2,
  },
  dishStatus: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  problems: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  problem: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.75),
    color: theme.palette.error.main,
    fontSize: "0.875rem",
  },
  footnote: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
  },
  empty: {
    padding: theme.spacing(6, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 12,
  },
  progress: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
}));

const DishRow = ({ dish }) => {
  const classes = useStyles();
  const dockets = docketSummary(dish.dockets);

  return (
    <div className={classes.dish}>
      <div className={classes.dishBody}>
        <div className={classes.dishName}>{dish.name}</div>
        {dockets && <div className={classes.dishNote}>{dockets}</div>}
        {dish.detail && <div className={classes.dishNote}>{dish.detail}</div>}
      </div>
      <div className={classes.dishStatus}>
        <Chip
          color={severityColor(dish.severity)}
          label={severityLabel(dish.severity)}
          size="small"
          sx={{ fontWeight: 700 }}
          variant={dish.severity === "missing" ? "filled" : "outlined"}
        />
        {dish.kdsText && <span className={classes.dishNote}>{dish.kdsText}</span>}
      </div>
    </div>
  );
};

DishRow.propTypes = {
  dish: PropTypes.shape({
    name: PropTypes.string.isRequired,
    severity: PropTypes.string,
    detail: PropTypes.string,
    kdsText: PropTypes.string,
    dockets: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, state: PropTypes.string })),
  }).isRequired,
};

const KitchenTicket = ({ status, now, showDevice }) => {
  const classes = useStyles();
  const problem = hasProblems(status);
  const kds = kdsChip(status.kdsState, problem);
  const ticketClass = problem
    ? classes.ticketProblem
    : status.severity === "inProgress"
      ? classes.ticketInProgress
      : "";

  return (
    <article className={`${classes.ticket} ${ticketClass}`} data-testid="kitchen-ticket">
      <header className={classes.ticketHeader}>
        <div>
          {status.tableNumber && <div className={classes.table}>Table {status.tableNumber}</div>}
          <div className={classes.orderRef}>Order {shortOrderNumber(status.orderNumber)}</div>
        </div>
        <div className={classes.ticketMeta}>
          <span className={`${classes.age} ${problem ? classes.ageProblem : ""}`}>
            {ageText(status.dispatchedAt, now)}
          </span>
          {kds && <Chip color={kds.color} label={kds.label} size="small" variant="outlined" />}
        </div>
      </header>

      {status.dishes.length === 0 && (
        <Typography color="error" sx={{ py: 1 }}>
          No dish reached a kitchen docket
        </Typography>
      )}
      {status.dishes.map((dish) => (
        <DishRow dish={dish} key={dish.id} />
      ))}

      {problem && (
        <div className={classes.problems}>
          {status.problems.map((item, index) => (
            <div className={classes.problem} key={`${item.kind}-${index}`}>
              <WarningAmberIcon fontSize="small" />
              <span>{item.message}</span>
            </div>
          ))}
        </div>
      )}

      {(status.resolvedAt || showDevice) && (
        <div className={classes.footnote}>
          {status.resolvedAt && <>Marked as handled {ageText(status.resolvedAt, now)}</>}
          {status.resolvedAt && showDevice && " · "}
          {showDevice && <>Sent from {status.deviceName || "a POS"}</>}
        </div>
      )}
    </article>
  );
};

const statusShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  orderNumber: PropTypes.string,
  tableNumber: PropTypes.string,
  dispatchedAt: PropTypes.number,
  resolvedAt: PropTypes.number,
  severity: PropTypes.string,
  kdsState: PropTypes.string,
  deviceId: PropTypes.string,
  deviceName: PropTypes.string,
  problems: PropTypes.arrayOf(PropTypes.shape({ kind: PropTypes.string, message: PropTypes.string })),
  dishes: PropTypes.arrayOf(PropTypes.object),
});

KitchenTicket.propTypes = {
  status: statusShape.isRequired,
  now: PropTypes.number.isRequired,
  showDevice: PropTypes.bool,
};

/**
 * The POS Kitchen Status board, for the whole of today and every POS at once.
 *
 * Each iPad POS works out where its kitchen dockets stand and uploads it once a
 * minute; this page only renders what they sent. Reprinting, resending to the
 * kitchen display and marking a ticket handled stay on the POS, which is the
 * device that owns the printers.
 */
const KitchenStatus = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const user = useSelector((state) => state.account.user);
  const hasIdentity = Boolean(user?.email && user?.restaurantId);
  const [problemsOnly, setProblemsOnly] = useState(false);

  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const isLg = useMediaQuery(theme.breakpoints.up("lg"));
  const isXl = useMediaQuery(theme.breakpoints.up("xl"));
  const columnCount = isXl ? 4 : isLg ? 3 : isMd ? 2 : 1;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    // The endpoint uses the session's restaurant; its cache must follow that scope.
    queryKey: ["kitchenStatus", user?.email, user?.restaurantId],
    queryFn: ({ signal }) => getKitchenStatus({ signal }),
    enabled: hasIdentity,
    refetchInterval: REFRESH_MS,
    staleTime: 0,
    retry: 2,
  });

  const statuses = useMemo(() => (data && data.statuses) || [], [data]);
  const devices = (data && data.devices) || [];
  const now = Date.now();
  const problemCount = statuses.filter(hasProblems).length;
  const visible = visibleStatuses(statuses, problemsOnly);
  const columns = dealIntoColumns(visible, columnCount);

  // Navigating has to happen after render, not during it.
  useEffect(() => {
    const status = error && error.response && error.response.status;
    if (status === 401 || status === 403) {
      history.push("/sign-in");
    }
  }, [error, history]);

  return (
    <div className={classes.root}>
      <header className={classes.header}>
        <div>
          <Typography variant="h4">Kitchen status</Typography>
          <Typography
            className={`${classes.subtitle} ${problemCount > 0 ? classes.subtitleProblem : ""}`}
            variant="body1"
          >
            {problemCount > 0
              ? `${statuses.length} orders today · ${problemCount} need attention`
              : `${statuses.length} orders today · all dockets accounted for`}
          </Typography>
        </div>
        <div className={classes.controls}>
          <ToggleButtonGroup
            exclusive
            onChange={(event, value) => value !== null && setProblemsOnly(value === "problems")}
            size="small"
            value={problemsOnly ? "problems" : "all"}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="problems">Problems</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <span>
              <IconButton aria-label="refresh" disabled={!hasIdentity || isFetching} onClick={() => refetch()}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </header>

      {devices.length > 0 && (
        <div className={classes.devices}>
          {devices.map((device) => {
            const stale = isDeviceStale(device, now);
            return (
              <Chip
                color={stale ? "warning" : "default"}
                icon={stale ? <WarningAmberIcon /> : undefined}
                key={device.deviceId}
                label={`${device.deviceName || "POS"} · reported ${ageText(device.reportedAt, now)}${
                  stale ? " - may be out of date" : ""
                }`}
                size="small"
                variant="outlined"
              />
            );
          })}
        </div>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load kitchen status. Retrying automatically.
        </Alert>
      )}

      {!hasIdentity || isLoading ? (
        <div className={classes.progress}>
          <CircularProgress />
        </div>
      ) : visible.length === 0 ? (
        <p className={classes.empty}>
          {devices.length === 0
            ? "No POS has reported kitchen status today. The POS sends it once it is updated to a build with kitchen status upload."
            : problemsOnly
              ? "Every kitchen docket sent today has printed."
              : "No kitchen dockets have been sent today."}
        </p>
      ) : (
        <div className={classes.columns}>
          {columns.map((column, index) => (
            <div className={classes.column} key={index}>
              {column.map((status) => (
                <KitchenTicket
                  key={`${status.deviceId}-${status.id}`}
                  now={now}
                  showDevice={devices.length > 1}
                  status={status}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KitchenStatus;
